/**
 * Lab Member.rate[] for downline Info (Backend-UI view-member parity).
 * Prefer hierarchy `rate` when present; else synthesize from share_* bp.
 */
export const LAB_GAME_IDS = {
  sportsBook: '5b36053e46123555cca93993',
  liveCasino: '5a818572650cb46dd9b1a25a',
} as const

export type LabRateRow = {
  _id: string
  game: { _id: string; name: string }
  parentRatePercent: number
  memberRatePercent: number
  memberSharePercentage: number
  parentSharePercentage: number
  memberAssignedRate: number
  maxRate: number
}

export type LabRateParentShares = {
  shareBp: number
  shareCasinoBp: number
}

export function bpToLabFraction(bp: number): number {
  const n = Number(bp)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.min(1, n / 10_000)
}

function rateRow(opts: {
  nodeId: string
  slot: 'live-casino' | 'sportsbook'
  gameId: string
  gameName: string
  memberBp: number
  parentBp: number
}): LabRateRow {
  const member = bpToLabFraction(opts.memberBp)
  const parent = bpToLabFraction(opts.parentBp)
  return {
    _id: `${opts.nodeId}:${opts.slot}`,
    game: { _id: opts.gameId, name: opts.gameName },
    parentRatePercent: parent,
    memberRatePercent: member,
    memberSharePercentage: member,
    parentSharePercentage: parent,
    memberAssignedRate: 0,
    maxRate: 100,
  }
}

/** Live Casino then SportsBook — Lab Info order; Poker/Roulette omitted. */
export function labRateFromShares(opts: {
  nodeId: string
  shareBp: number
  shareCasinoBp: number
  parent?: LabRateParentShares | null
}): LabRateRow[] {
  const parentSb = opts.parent?.shareBp ?? 0
  const parentCasino = opts.parent?.shareCasinoBp ?? parentSb
  return [
    rateRow({
      nodeId: opts.nodeId,
      slot: 'live-casino',
      gameId: LAB_GAME_IDS.liveCasino,
      gameName: 'Live Casino',
      memberBp: opts.shareCasinoBp,
      parentBp: parentCasino,
    }),
    rateRow({
      nodeId: opts.nodeId,
      slot: 'sportsbook',
      gameId: LAB_GAME_IDS.sportsBook,
      gameName: 'SportsBook',
      memberBp: opts.shareBp,
      parentBp: parentSb,
    }),
  ]
}

export function labRateGameLabel(name: string): string {
  if (name === 'TeenPatti') return 'Club Casino'
  return name
}

export function labRateRowsForInfo(rate: LabRateRow[] | undefined): LabRateRow[] {
  return (rate ?? []).filter((r) => {
    const n = r.game?.name ?? ''
    return n !== 'Poker' && n !== 'Roulette'
  })
}

/** INR → memberRatePercent; POINTS / POINT@1 → share × 100. */
export function labRateDisplayValue(
  rate: LabRateRow,
  currencyRaw?: string | null,
): number {
  const ccy = String(currencyRaw ?? 'INR').toUpperCase()
  if (ccy === 'INR') return Number(rate.memberRatePercent ?? 0)
  return Number(rate.memberSharePercentage ?? 0) * 100
}
